import { Component, computed, input } from '@angular/core';

/** Desenhos das opções de formato da mídia (painel da direita e configurações do documento). */
export type LookGlyphName =
    | 'size-1'
    | 'size-2'
    | 'size-3'
    | 'cols-1'
    | 'cols-2'
    | 'cols-3'
    | 'fit-fill'
    | 'fit-full'
    | 'caption-figure'
    | 'caption-band'
    | 'place-below'
    | 'place-side';

/** Tom de cada peça: todos pintam com `currentColor`, então a opção marcada fica azul sozinha. */
type Tone = 'band' | 'photo' | 'peak' | 'line' | 'rule' | 'strip';

interface Piece {
    tone: Tone;
    d: string;
}

type Corners = 'all' | 'top' | 'bottom';

/** Retângulo com os cantos arredondados (todos, só os de cima ou só os de baixo). */
function box(x: number, y: number, w: number, h: number, r: number, corners: Corners = 'all'): string {
    const top = corners !== 'bottom';
    const bottom = corners !== 'top';
    const n = (value: number) => Math.round(value * 100) / 100;

    return [
        `M${n(x)},${n(top ? y + r : y)}`,
        top ? `A${r},${r} 0 0 1 ${n(x + r)},${n(y)}` : '',
        `H${n(top ? x + w - r : x + w)}`,
        top ? `A${r},${r} 0 0 1 ${n(x + w)},${n(y + r)}` : '',
        `V${n(bottom ? y + h - r : y + h)}`,
        bottom ? `A${r},${r} 0 0 1 ${n(x + w - r)},${n(y + h)}` : '',
        `H${n(bottom ? x + r : x)}`,
        bottom ? `A${r},${r} 0 0 1 ${n(x)},${n(y + h - r)}` : '',
        'Z',
    ].join('');
}

/** Foto: o fundo, uma serra e o sol, o símbolo de imagem que todo mundo reconhece. */
function photo(x: number, y: number, w: number, h: number, corners: Corners = 'all'): Piece[] {
    const r = Math.min(2.5, w / 5, h / 5);
    const n = (value: number) => Math.round(value * 100) / 100;
    const sun = Math.max(0.9, Math.min(w, h * 1.4) * 0.085);
    const cx = x + w * 0.74;
    const cy = y + h * 0.3;

    return [
        { tone: 'photo', d: box(x, y, w, h, r, corners) },
        {
            tone: 'peak',
            d:
                `M${n(x + w * 0.1)},${n(y + h)}` +
                `L${n(x + w * 0.38)},${n(y + h * 0.46)}` +
                `L${n(x + w * 0.58)},${n(y + h * 0.76)}` +
                `L${n(x + w * 0.7)},${n(y + h * 0.6)}` +
                `L${n(x + w * 0.9)},${n(y + h)}Z`,
        },
        {
            tone: 'peak',
            d: `M${n(cx - sun)},${n(cy)}a${n(sun)},${n(sun)} 0 1,0 ${n(sun * 2)},0a${n(sun)},${n(sun)} 0 1,0 ${n(-sun * 2)},0Z`,
        },
    ];
}

/** Linha de texto: uma pílula. */
function line(x: number, y: number, w: number, tone: Tone = 'line'): Piece {
    return { tone, d: box(x, y, w, 2.4, 1.2) };
}

/** Faixa cinza da coluna (o slot onde a foto reduzida fica centralizada). */
function band(): Piece {
    return { tone: 'band', d: box(0, 3, 40, 22, 3) };
}

/** Grade de fotos: duas linhas de `columns` fotos. */
function grid(columns: number): Piece[] {
    const gap = 2;
    const width = (40 - gap * (columns - 1)) / columns;
    const pieces: Piece[] = [];
    for (const y of [1, 15])
        for (let i = 0; i < columns; i++) pieces.push(...photo(i * (width + gap), y, width, 12));
    return pieces;
}

const GLYPHS: Record<LookGlyphName, Piece[]> = {
    // Tamanho: a foto na largura toda, na metade e num terço, dentro da faixa.
    'size-1': photo(0, 3, 40, 22),
    'size-2': [band(), ...photo(10, 7, 20, 14)],
    'size-3': [band(), ...photo(13.5, 9.5, 13, 9)],

    // Fotos por linha.
    'cols-1': grid(1),
    'cols-2': grid(2),
    'cols-3': grid(3),

    // Enquadramento: a foto cobre o slot, ou aparece inteira com sobra dos lados.
    'fit-fill': photo(0, 3, 40, 22),
    'fit-full': [band(), ...photo(14, 3, 12, 22)],

    // Estilo da legenda.
    'caption-figure': [
        ...photo(0, 0, 40, 15),
        { tone: 'rule', d: box(0.4, 18.4, 1.6, 9, 0.8) },
        line(5, 19, 25),
        line(5, 23.2, 16),
    ],
    'caption-band': [
        ...photo(0, 0, 40, 16, 'top'),
        { tone: 'strip', d: box(0, 16, 40, 12, 3, 'bottom') },
        line(4, 19.6, 24, 'rule'),
        line(4, 23.4, 14),
    ],

    // Posição da legenda na mídia única.
    'place-below': [...photo(5, 0, 30, 15), line(5, 18.6, 30), line(5, 22.8, 19)],
    'place-side': [
        ...photo(0, 6, 19, 16),
        line(22, 7.5, 18),
        line(22, 11.8, 15),
        line(22, 16.1, 17),
        line(22, 20.4, 10),
    ],
};

/**
 * Desenho de uma opção de formato da mídia (tamanho, fotos por linha, enquadramento, estilo e
 * posição da legenda). Traço leve e cantos redondos, no lugar dos blocos cinza de antes, que
 * pareciam duros. Tudo em `currentColor` com transparência: a opção marcada (texto azul)
 * pinta o desenho de azul sem regra própria, e o tema escuro acompanha.
 */
@Component({
    selector: 'look-glyph',
    template: `
        <svg viewBox="0 0 40 28" aria-hidden="true" focusable="false">
            @for (piece of pieces(); track $index) {
                <path [attr.d]="piece.d" [attr.class]="piece.tone" />
            }
        </svg>
    `,
    styles: `
        :host {
            display: inline-block;
            width: 2.5rem;
            height: 1.75rem;
            line-height: 0;
            flex-shrink: 0;
        }

        svg {
            display: block;
            width: 100%;
            height: 100%;
        }

        path {
            fill: currentColor;
        }

        .band {
            opacity: 0.07;
        }

        .photo {
            opacity: 0.16;
        }

        .peak {
            opacity: 0.34;
        }

        .line {
            opacity: 0.26;
        }

        .strip {
            opacity: 0.1;
        }

        .rule {
            opacity: 0.5;
        }
    `,
})
export class LookGlyphComponent {
    public name = input.required<LookGlyphName>();
    public pieces = computed(() => GLYPHS[this.name()] ?? []);
}
